/**
 * Icon + project-mark set.
 *
 * Every glyph is drawn on the same 24x24 grid with a 1.5 stroke and
 * currentColor, so marks inherit whatever accent the surrounding card sets.
 */

const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
}

const Svg = ({ size = 24, children, ...rest }) => (
  <svg {...base} width={size} height={size} {...rest}>
    {children}
  </svg>
)

/* --- Project marks -------------------------------------------------------- */

const marks = {
  // Waveform through a rounded field — speech captured on the device itself.
  scribatic: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="4" />
      <path d="M7 10.5v3M10 8.5v7M13 10v4M16.5 11.5v1" />
    </>
  ),
  // Globe crossed by a seismic trace.
  eqm: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.8 9.5h4.4l1.6 4 2.3-7 1.7 5.5 1.2-2.5h4.8" />
    </>
  ),
  // Stacked recall cards, the top one lifted.
  activedecks: (
    <>
      <rect x="7.5" y="3.2" width="13" height="13" rx="3" />
      <path d="M16.5 19.5a2 2 0 0 1-2 2h-8a3 3 0 0 1-3-3v-8a2 2 0 0 1 2-2" />
      <path d="M11 9.8h6" />
    </>
  ),
  // Framed nightscape: horizon under stars.
  bytevisions: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M3 15.5l4.5-4 3.5 3 3-2.5L21 17" />
      <path d="M8 8h.01M15.5 7.5h.01" />
    </>
  ),
  // The glide-path V, trailed by voice bars.
  voxyglider: (
    <>
      <path d="M9 6.5l4.5 11 7-12" />
      <path d="M6 12v4.5M3 10v5" />
    </>
  ),
  // A cursor band across the staff, a note just ahead of it.
  enzabeat: (
    <>
      <path d="M3 9.5h18M3 15h18" />
      <rect x="14" y="4" width="4" height="16" rx="2" />
      <circle cx="7.5" cy="15" r="2" />
      <path d="M9.5 15V8" />
    </>
  ),
  // Droplet feeding a leaf — irrigation under control.
  hydroponix: (
    <>
      <path d="M8 3.5s4 4.2 4 6.8a4 4 0 0 1-8 0C4 7.7 8 3.5 8 3.5Z" />
      <path d="M20.5 12.5c0 4.4-3.2 7.5-7.5 7.5-.6 0-1.2-.06-1.7-.2" />
      <path d="M13 20c0-3.5 2.6-6.2 7.5-7.5" />
    </>
  ),
}

export const ProjectMark = ({ name, size = 24 }) => (
  <Svg size={size}>{marks[name] || marks.scribatic}</Svg>
)

/* --- UI icons ------------------------------------------------------------- */

export const ArrowRight = (p) => (
  <Svg size={16} {...p}>
    <path d="M4 12h15M13 6l6 6-6 6" />
  </Svg>
)

export const ArrowDown = (p) => (
  <Svg size={16} {...p}>
    <path d="M12 4v15M6 13l6 6 6-6" />
  </Svg>
)

export const External = (p) => (
  <Svg size={14} {...p}>
    <path d="M14 4h6v6M20 4l-9 9" />
    <path d="M18 14v4.5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" />
  </Svg>
)

export const Code = (p) => (
  <Svg size={14} {...p}>
    <path d="M9 6l-5 6 5 6M15 6l5 6-5 6" />
  </Svg>
)

export const Store = (p) => (
  <Svg size={14} {...p}>
    <path d="M4.5 9.5V19a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1V9.5" />
    <path d="M3 9.5 5 4h14l2 5.5a3 3 0 0 1-5.5 1.6 3 3 0 0 1-5 0 3 3 0 0 1-5.5-1.6Z" />
  </Svg>
)

export const Doc = (p) => (
  <Svg size={14} {...p}>
    <path d="M14 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8Z" />
    <path d="M14 3.5V8h4.5M9 13h6M9 16.5h4" />
  </Svg>
)

export const Github = (p) => (
  <Svg size={19} {...p}>
    <path d="M9 19.5c-4.4 1.4-4.4-2.4-6-3m12 5v-3.6a3.1 3.1 0 0 0-.9-2.4c2.9-.3 6-1.4 6-6.4a5 5 0 0 0-1.4-3.4 4.6 4.6 0 0 0-.1-3.5s-1.1-.3-3.6 1.4a12.3 12.3 0 0 0-6.4 0C6.1 1.4 5 1.7 5 1.7a4.6 4.6 0 0 0-.1 3.5A5 5 0 0 0 3.5 8.7c0 5 3 6.1 5.9 6.4a3.1 3.1 0 0 0-.9 2.4V21" />
  </Svg>
)

export const Linkedin = (p) => (
  <Svg size={19} {...p}>
    <path d="M16.5 8.5A5.5 5.5 0 0 1 22 14v6.5h-4V14a1.5 1.5 0 0 0-3 0v6.5h-4V14a5.5 5.5 0 0 1 5.5-5.5Z" />
    <rect x="2" y="9" width="4" height="11.5" rx="0.5" />
    <circle cx="4" cy="4.5" r="2" />
  </Svg>
)

export const Twitter = (p) => (
  <Svg size={19} {...p}>
    <path d="M3 3.5h4.2l5.3 7 5.9-7H21l-7.3 8.6L21.5 20.5h-4.2l-5.6-7.4-6.2 7.4H3l7.7-9.1Z" />
  </Svg>
)

export const Mail = (p) => (
  <Svg size={19} {...p}>
    <rect x="2.5" y="5" width="19" height="14" rx="2" />
    <path d="m3 7 8.2 5.6a1.5 1.5 0 0 0 1.6 0L21 7" />
  </Svg>
)

export const Sun = (p) => (
  <Svg size={17} {...p}>
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.5v2M12 19.5v2M4.6 4.6l1.4 1.4M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4 6 18M18 6l1.4-1.4" />
  </Svg>
)

export const Moon = (p) => (
  <Svg size={17} {...p}>
    <path d="M20.5 14.3A8.5 8.5 0 0 1 9.7 3.5a8.5 8.5 0 1 0 10.8 10.8Z" />
  </Svg>
)

export const linkIcon = { site: External, repo: Code, store: Store, doc: Doc }
